import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getFlowsheets } from '../services/encounterService';
import { getFacility } from '../services/facilityService';
import { getDoctorsByFacility } from '../services/userService';
import { getCareTeam } from '../services/careCoordinationService';
import { selectUserFacilities } from '../redux/slices/facilitiesSlice';
// Removed Redux auth selectors - now using useAuth() hook
import { useAuth } from '../context/AuthContext';
import { flattenDepartments, flattenRooms } from '../utils/firebaseUtils.js';

/**
 * Custom hook for managing encounter-related data
 * Optimized for performance with lazy loading support
 * 
 * @param {Object} encounter - The encounter object
 * @param {Object} patient - The patient object
 * @param {string} mode - 'edit' or 'create'
 * @param {Object} options - Configuration options
 * @param {boolean} options.lazyLoadDoctors - If true, doctors won't be auto-fetched (default: false)
 * @param {boolean} options.lazyLoadFlowsheets - If true, flowsheets won't be auto-fetched (default: true)
 * @param {boolean} options.lazyLoadCareTeam - If true, care team won't be auto-fetched (default: true)
 */
export const useEncounterData = (encounter, patient, mode = 'edit', options = {}) => {
  const {
    lazyLoadDoctors = false, // Doctors are needed for Clinical Data tab, keep eager by default
    lazyLoadFlowsheets = true, // Flowsheets can be lazy loaded
    lazyLoadCareTeam = true, // Care team can be lazy loaded
  } = options;

  const userFacilities = useSelector(selectUserFacilities);
  const { user } = useAuth();
  
  // State for data
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorsFetched, setDoctorsFetched] = useState(false);
  const [attendingProvider, setAttendingProvider] = useState(null);
  const [careTeamLoading, setCareTeamLoading] = useState(false);
  const [careTeamFetched, setCareTeamFetched] = useState(false);
  const [flowsheetRows, setFlowsheetRows] = useState([]);
  const [flowsheetLoading, setFlowsheetLoading] = useState(false);
  const [flowsheetError, setFlowsheetError] = useState('');
  const [flowsheetsFetched, setFlowsheetsFetched] = useState(false);
  const [facilityName, setFacilityName] = useState('');
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  
  // Refs to prevent infinite loops
  const isFetchingDepartments = useRef(false);
  const lastFacilityIdRef = useRef(null);
  const hasMountedRef = useRef(false);
  const lastEncounterIdRef = useRef(null);
  const lastPatientIdRef = useRef(null);

  // Memoized values to prevent unnecessary re-renders
  const encounterId = encounter?.id;
  const patientId = encounter?.patientId || patient?.id;
  const facilityId = encounter?.facilityId || patient?.facilityId;

  // Clear all data when patient or encounter changes
  const clearData = useCallback(() => {
    console.log('useEncounterData: Clearing data due to patient/encounter change');
    setDoctors([]);
    setDoctorsFetched(false);
    setDoctorsLoading(false);
    setAttendingProvider(null);
    setCareTeamFetched(false);
    setCareTeamLoading(false);
    setFlowsheetRows([]);
    setFlowsheetLoading(false);
    setFlowsheetError('');
    setFlowsheetsFetched(false);
    setFacilityName('');
    setDepartments([]);
    setAllRooms([]);
  }, []);

  // Listen for cache clearing events
  useEffect(() => {
    const handleCacheClear = (event) => {
      const { patientId: eventPatientId, encounterId: eventEncounterId } = event.detail;
      
      // Clear data if the event is for our current patient/encounter
      if (eventPatientId === patientId || eventEncounterId === encounterId) {
        console.log('useEncounterData: Received cache clear event, clearing data');
        clearData();
      }
    };

    window.addEventListener('clearEncounterCache', handleCacheClear);
    
    return () => {
      window.removeEventListener('clearEncounterCache', handleCacheClear);
    };
  }, [patientId, encounterId, clearData]);

  // Clear data when patient or encounter changes
  useEffect(() => {
    if (lastPatientIdRef.current !== patientId || lastEncounterIdRef.current !== encounterId) {
      console.log(`useEncounterData: Patient/encounter changed from ${lastPatientIdRef.current}/${lastEncounterIdRef.current} to ${patientId}/${encounterId}`);
      clearData();
      lastPatientIdRef.current = patientId;
      lastEncounterIdRef.current = encounterId;
    }
  }, [patientId, encounterId, clearData]);

  // On-demand function to fetch doctors
  const fetchDoctors = useCallback(async () => {
    if (!facilityId) {
      console.log('useEncounterData: No facilityId, skipping doctors fetch');
      return [];
    }
    
    if (doctorsFetched && doctors.length > 0) {
      console.log('useEncounterData: Using cached doctors data');
      return doctors;
    }
    
    setDoctorsLoading(true);
    try {
      console.log('useEncounterData: Fetching doctors for facility:', facilityId);
      const doctorsData = await getDoctorsByFacility(facilityId);
      const doctorsList = doctorsData?.doctors || [];
      
      console.log('useEncounterData: Fetched doctors list:', doctorsList.length, 'doctors/attending physicians');
      
      setDoctors(doctorsList);
      setDoctorsFetched(true);
      return doctorsList;
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
      return [];
    } finally {
      setDoctorsLoading(false);
    }
  }, [facilityId, doctorsFetched, doctors]);

  // Auto-fetch doctors if not lazy loading
  useEffect(() => {
    if (!lazyLoadDoctors && user?.uid && facilityId && !doctorsFetched) {
      fetchDoctors();
    }
  }, [lazyLoadDoctors, user?.uid, facilityId, doctorsFetched, fetchDoctors]);

  // On-demand function to fetch care team data
  const fetchCareTeamData = useCallback(async () => {
    if (!patientId) return null;
    
    if (careTeamFetched && attendingProvider) {
      console.log('useEncounterData: Using cached care team data');
      return attendingProvider;
    }
    
    setCareTeamLoading(true);
    try {
      console.log('useEncounterData: Fetching care team for patient:', patientId);
      const careTeam = await getCareTeam(patientId);
      if (careTeam?.members) {
        const attending = careTeam.members.find(member => 
          member.role === 'attending_physician' || member.role === 'ATTENDING_PHYSICIAN'
        );
        setAttendingProvider(attending || null);
        setCareTeamFetched(true);
        return attending;
      }
      setCareTeamFetched(true);
      return null;
    } catch (error) {
      console.error('Error fetching care team:', error);
      return null;
    } finally {
      setCareTeamLoading(false);
    }
  }, [patientId, careTeamFetched, attendingProvider]);

  // Auto-fetch care team if not lazy loading
  useEffect(() => {
    if (!lazyLoadCareTeam && hasMountedRef.current && patientId && !careTeamFetched) {
      fetchCareTeamData();
    }
  }, [lazyLoadCareTeam, patientId, careTeamFetched, fetchCareTeamData]);

  // On-demand function to fetch flowsheets
  const fetchFlowsheetsData = useCallback(async (options = {}) => {
    if (!encounterId) return [];
    
    if (flowsheetsFetched && flowsheetRows.length > 0 && !options.forceRefresh) {
      console.log('useEncounterData: Using cached flowsheets data');
      return flowsheetRows;
    }
    
    setFlowsheetLoading(true);
    setFlowsheetError('');
    
    try {
      console.log('useEncounterData: Fetching flowsheets for encounter:', encounterId);
      const flowsheets = await getFlowsheets(encounterId, { hours: options.hours || 24 });
      
      setFlowsheetRows(flowsheets || []);
      setFlowsheetsFetched(true);
      return flowsheets || [];
    } catch (err) {
      console.error('Error fetching flowsheets:', err);
      setFlowsheetError('Failed to fetch flowsheets.');
      return [];
    } finally {
      setFlowsheetLoading(false);
    }
  }, [encounterId, flowsheetsFetched, flowsheetRows]);

  // Auto-fetch flowsheets if not lazy loading
  useEffect(() => {
    if (!lazyLoadFlowsheets && hasMountedRef.current && encounterId && !flowsheetsFetched) {
      fetchFlowsheetsData();
    }
  }, [lazyLoadFlowsheets, encounterId, flowsheetsFetched, fetchFlowsheetsData]);

  // Combined fetch for facility data (name, departments, rooms) to avoid duplicate calls
  const fetchFacilityData = useCallback(async (targetFacilityId) => {
    if (!targetFacilityId) {
      return;
    }
    
    // Prevent concurrent fetches
    if (isFetchingDepartments.current) {
      console.log('Already fetching facility data, skipping...');
      return;
    }
    
    isFetchingDepartments.current = true;
    
    try {
      // Single API call to get facility data
      const facility = await getFacility(targetFacilityId);
      
      if (facility) {
        // Set facility name
        const name = facility?.name || '';
        setFacilityName(prev => prev !== name ? name : prev);
        
        // Set departments and rooms
        const flatDepts = flattenDepartments(facility);
        const flatRooms = flattenRooms(facility);
        
        setDepartments(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(flatDepts)) {
            return flatDepts;
          }
          return prev;
        });
        
        setAllRooms(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(flatRooms)) {
            return flatRooms;
          }
          return prev;
        });
      } else {
        setFacilityName('');
        setDepartments([]);
        setAllRooms([]);
      }
    } catch (e) {
      console.error('Error fetching facility data:', e);
      setFacilityName('');
      setDepartments([]);
      setAllRooms([]);
    } finally {
      isFetchingDepartments.current = false;
    }
  }, []);

  // Initialize facility data when facility ID changes (single effect for all facility data)
  useEffect(() => {
    // If encounter already has facility name, use it
    if (encounter?.facilityName) {
      setFacilityName(prev => prev !== encounter.facilityName ? encounter.facilityName : prev);
    }
    
    // Use patient's facility ID first, then fall back to user's default facility
    const effectiveFacilityId = encounter?.facilityId || user?.defaultFacility;
    
    if (effectiveFacilityId && lastFacilityIdRef.current !== effectiveFacilityId) {
      console.log('Facility ID changed, fetching facility data for:', effectiveFacilityId);
      lastFacilityIdRef.current = effectiveFacilityId;
      fetchFacilityData(effectiveFacilityId);
    }
  }, [encounter?.facilityId, encounter?.facilityName, user?.defaultFacility, fetchFacilityData]);

  // Legacy function for manual department refresh (kept for backwards compatibility)
  const fetchDepartments = fetchFacilityData;

  // Fetch rooms when department changes
  const fetchRooms = useCallback((departmentId) => {
    if (!departmentId || !allRooms || allRooms.length === 0) {
      setRooms([]);
      return;
    }
    
    const filteredRooms = allRooms.filter(r => 
      r.departmentId === departmentId || r.departmentName === departmentId
    );
    
    setRooms(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(filteredRooms)) {
        return filteredRooms;
      }
      return prev;
    });
  }, [allRooms]);

  // Set mounted flag
  useEffect(() => {
    hasMountedRef.current = true;
    return () => {
      hasMountedRef.current = false;
    };
  }, []);

  return {
    // Data
    doctors,
    attendingProvider,
    flowsheetRows,
    flowsheetLoading,
    flowsheetError,
    facilityName,
    departments,
    rooms,
    allRooms,
    userFacilities: userFacilities,
    
    // Functions for lazy loading
    fetchDepartments,
    fetchRooms,
    fetchDoctors,
    fetchCareTeamData,
    fetchFlowsheetsData,
    clearData,
    
    // Loading states
    isLoading: flowsheetLoading || doctorsLoading || careTeamLoading,
    doctorsLoading,
    careTeamLoading,
    
    // Fetched states (useful for checking if data has been loaded)
    doctorsFetched,
    careTeamFetched,
    flowsheetsFetched,
  };
}; 